import { Directive, EmbeddedViewRef, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { Subject, takeUntil } from 'rxjs';
import { FeatureFlagKey } from '../types/feature-flag-key';

/**
 * Show content only when flag is disabled - useful for if/else or refactoring existing component
 *
 * Example usages:
 *
 * ```
 * <div *ifFeatureFlagDisabled="'featureFlagKey'">
 *  content will be shown only when feature is _disabled_
 * </div>
 * ```
 */
@Directive({
  selector: '[ifFeatureFlagDisabled]',
  standalone: false,
})
export class FeatureFlagDisabledDirective implements OnInit, OnDestroy {
  /**
   * Feature flag key name
   */
  @Input() public ifFeatureFlagDisabled: FeatureFlagKey | undefined;

  private readonly _onDestroy$ = new Subject<void>();
  private _viewRef: EmbeddedViewRef<any> | undefined;

  constructor(
    private readonly template: TemplateRef<any>,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly featureFlagService: FeatureFlagsService,
  ) {}

  ngOnInit(): void {
    if (this.ifFeatureFlagDisabled === undefined) {
      return;
    }

    this.featureFlagService
      .isFeatureEnabled$(this.ifFeatureFlagDisabled)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(isEnabled => {
        if (!isEnabled) {
          if (this._viewRef != null) {
            // already created
            return;
          }

          this._viewRef = this.viewContainerRef.createEmbeddedView(this.template);
          this._viewRef.detectChanges();
        } else if (this._viewRef != null) {
          this._viewRef.destroy();
          this.viewContainerRef.clear();
          this._viewRef = undefined;
        }
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
    this._viewRef?.destroy();
  }
}
